import { useState } from 'react'

import { deformPoint, twistAngle, type Деформация } from '../../lib/physics'
import { Arrow, ArrowDefs, Choice, Predict, Slider } from './common'

/**
 * Виды деформаций (урок 34): брусок с сеткой растягивают, сжимают, изгибают, сдвигают,
 * закручивают. Видно, как смещаются частицы, и что сила упругости направлена против смещения.
 */

const X0 = 50
const ДЛИНА = 220
const ТОЛЩИНА = 60
const Y0 = 140

const ПРИМЕРЫ: Record<Деформация, string> = {
  растяжение: 'трос подъёмного крана, натянутая струна',
  сжатие: 'колонна под крышей, ножка стула',
  изгиб: 'лук, рельс под поездом, полка с книгами',
  сдвиг: 'бумага под ножницами, заклёпка',
  кручение: 'вал гребного винта, шуруп при закручивании',
}

function вЭкран([x, y]: [number, number]): string {
  return `${X0 + x * ДЛИНА},${Y0 + y * ТОЛЩИНА}`
}

export default function DeformLab() {
  const [вид, setВид] = useState<Деформация>('растяжение')
  const [s, setS] = useState(0.6)

  const линии: string[] = []
  if (вид !== 'кручение') {
    for (let j = 0; j <= 4; j++) {
      const y = -0.5 + j / 4
      const pts: string[] = []
      for (let i = 0; i <= 40; i++) pts.push(вЭкран(deformPoint(вид, i / 40, y, s)))
      линии.push(pts.join(' '))
    }
    for (let i = 0; i <= 10; i++) {
      const pts: string[] = []
      for (let j = 0; j <= 8; j++) pts.push(вЭкран(deformPoint(вид, i / 10, -0.5 + j / 8, s)))
      линии.push(pts.join(' '))
    }
  } else {
    // кручение, вид сбоку: образующие цилиндра идут по винтовой линии
    for (let k = 0; k < 12; k++) {
      const φ = (k / 12) * 2 * Math.PI
      let сегмент: string[] = []
      for (let i = 0; i <= 60; i++) {
        const x = i / 60
        const фаза = φ + twistAngle(x, s)
        if (Math.sin(фаза) > 0) сегмент.push(вЭкран([x, 0.5 * Math.cos(фаза)]))
        else if (сегмент.length) {
          линии.push(сегмент.join(' '))
          сегмент = []
        }
      }
      if (сегмент.length > 1) линии.push(сегмент.join(' '))
    }
  }

  // свободный конец: куда сместились частицы и куда тянет сила упругости
  const L = 60 * s
  const конец = deformPoint(вид, 1, 0, s)
  const [кx, кy] = [X0 + конец[0] * ДЛИНА, Y0 + конец[1] * ТОЛЩИНА]
  const верх = deformPoint(вид, 1, -0.5, s)
  const [вx, вy] = [X0 + верх[0] * ДЛИНА, Y0 + верх[1] * ТОЛЩИНА]
  let внешняя = { x1: кx + 8, y1: кy, x2: кx + 8 + L, y2: кy }
  let упругая = { x1: кx - 6, y1: кy + 14, x2: кx - 6 - L, y2: кy + 14 }
  if (вид === 'сжатие') {
    внешняя = { x1: кx + 8 + L, y1: кy, x2: кx + 8, y2: кy }
    упругая = { x1: кx - 6 - L, y1: кy + 14, x2: кx - 6, y2: кy + 14 }
  } else if (вид === 'изгиб') {
    внешняя = { x1: кx + 14, y1: кy, x2: кx + 14, y2: кy + L }
    упругая = { x1: кx - 10, y1: кy, x2: кx - 10, y2: кy - L }
  } else if (вид === 'сдвиг') {
    внешняя = { x1: вx - 40, y1: вy - 10, x2: вx - 40 + L, y2: вy - 10 }
    упругая = { x1: вx - 50, y1: вy + 12, x2: вx - 50 - L, y2: вy + 12 }
  }

  const угол = twistAngle(1, s)

  return (
    <div className="card">
      <svg className="lab-svg" viewBox="0 0 360 260" role="img" aria-label={`Брусок: ${вид}`}>
        <ArrowDefs />
        {вид === 'сдвиг' ? (
          <rect x={X0 - 10} y={Y0 + ТОЛЩИНА / 2} width={ДЛИНА + 30} height={10} className="fill-muted" />
        ) : (
          <rect x={X0 - 14} y={Y0 - 60} width={14} height={120} className="fill-muted" />
        )}
        {вид === 'кручение' && (
          <rect x={X0} y={Y0 - ТОЛЩИНА / 2} width={ДЛИНА} height={ТОЛЩИНА} className="fill-body" />
        )}
        {линии.map((p, i) => (
          <polyline key={i} points={p} fill="none" className="ink" strokeWidth={i < 5 && вид !== 'кручение' && (i === 0 || i === 4) ? 2 : 1} />
        ))}
        {вид === 'кручение' ? (
          <g transform={`translate(${X0 + ДЛИНА + 50} ${Y0})`}>
            <circle r={30} className="fill-body ink" strokeWidth={1.5} />
            <line x1={0} y1={0} x2={30 * Math.cos(-Math.PI / 2 + угол)} y2={30 * Math.sin(-Math.PI / 2 + угол)} className="ink-heavy" strokeWidth={2} />
            <line x1={0} y1={0} x2={0} y2={-30} className="ink-muted" strokeDasharray="3 3" />
            <text x={0} y={48} textAnchor="middle" className="lab-tick">торец</text>
          </g>
        ) : (
          <>
            <Arrow {...внешняя} kind="ink" label="сила" labelDx={4} labelDy={-6} />
            <Arrow {...упругая} kind="el" label="F упр" labelDx={-34} labelDy={16} />
          </>
        )}
      </svg>

      <div className="lab-caption">
        {вид === 'кручение'
          ? `Сечения поворачиваются тем сильнее, чем дальше от закреплённого конца: торец повёрнут на ${Math.round((угол * 180) / Math.PI)}°. Сила упругости стремится повернуть его обратно.`
          : 'Частицы бруска сместились — возникла сила упругости. Она направлена противоположно смещению и стремится вернуть брусок в прежнюю форму.'}
      </div>
      <div className="muted small">Примеры: {ПРИМЕРЫ[вид]}.</div>

      <Predict
        question="Куда направлена сила упругости?"
        options={['туда же, куда внешняя сила', 'противоположно смещению частиц', 'всегда вниз']}
        right={1}
        revealed={s > 0.2}
        resetKey={вид}
        explain="Сила упругости возникает из-за деформации и стремится вернуть телу прежнюю форму — поэтому она направлена против смещения частиц."
      />

      <div className="lab-sub">Деформация</div>
      <Choice
        options={(['растяжение', 'сжатие', 'изгиб', 'сдвиг', 'кручение'] as const).map((v) => ({ value: v, label: v }))}
        value={вид}
        onChange={setВид}
      />
      <Slider label="Сила воздействия" value={s} min={0} max={1} step={0.05} unit="" shown={`${Math.round(s * 100)}%`} onChange={setS} />
      <p className="muted small">
        Деформация — любое изменение формы или размеров тела. Изгиб сводится к растяжению и
        сжатию (верх прогнутого бруска растянут, низ сжат), кручение — к сдвигу.
      </p>
    </div>
  )
}
